<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import type { Chat as Conversation } from 'models'
    import { getConversationPreview } from 'composables'

    const props = withDefaults(
        defineProps<{
            conversation: Conversation
            active?: boolean
            editing?: boolean
            /** Show the "chat files" action. The host turns it off when the
             * caller cannot use the My files listing (anonymous users). */
            filesEnabled?: boolean
        }>(),
        {
            filesEnabled: true,
        },
    )

    const editingTitle = defineModel<string>('editingTitle', { default: '' })

    const emit = defineEmits<{
        select: []
        'edit-start': []
        'edit-confirm': []
        'edit-cancel': []
        'edit-keydown': [event: KeyboardEvent]
        delete: []
        files: []
    }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    const preview = computed(() => getConversationPreview(props.conversation))
</script>

<template>
    <div
        class="pk-chat-sidebar-item"
        :class="{ 'pk-chat-sidebar-item--active': active }">
        <template v-if="editing">
            <VvInputText
                v-model="editingTitle"
                name="conversation-title"
                class="pk-chat-sidebar-item__edit"
                modifiers="compact"
                autofocus
                select-on-focus
                :maxlength="255"
                :placeholder="$t('placeholder.insert')"
                @keydown="emit('edit-keydown', $event)" />
            <VvButtonGroup modifiers="compact">
                <VvButton
                    icon="ri:save-line"
                    :title="$t('action.save')"
                    modifiers="action-quiet-small"
                    @click="emit('edit-confirm')" />
                <VvButton
                    icon="ri:close-line"
                    :title="$t('action.cancel')"
                    modifiers="action-quiet-small"
                    @click="emit('edit-cancel')" />
            </VvButtonGroup>
        </template>
        <template v-else>
            <button
                type="button"
                class="pk-chat-sidebar-item__button"
                :aria-current="active ? 'page' : undefined"
                :title="preview"
                @click="emit('select')">
                {{ preview }}
            </button>
            <VvDropdown
                v-bind="{
                    placement: 'bottom-end',
                    modifiers: 'menu',
                    flip: true,
                    offset: 3,
                    strategy: 'fixed',
                }"
                class="pk-chat-sidebar-item__actions">
                <VvButton
                    icon="ri:more-2-fill"
                    :title="$t('action.moreActions')"
                    modifiers="action-quiet-small" />
                <template #items>
                    <VvDropdownAction
                        v-if="filesEnabled"
                        @click="emit('files')">
                        <VvIcon name="ri:attachment-2" />
                        {{ $t('userFiles.chatFilesTitle') }}
                    </VvDropdownAction>
                    <VvDropdownAction @click="emit('edit-start')">
                        <VvIcon name="ri:pencil-line" />
                        {{ $t('action.renameChat') }}
                    </VvDropdownAction>
                    <VvDropdownAction @click="emit('delete')">
                        <VvIcon name="ri:delete-bin-line" />
                        {{ $t('action.delete') }}
                    </VvDropdownAction>
                </template>
            </VvDropdown>
        </template>
    </div>
</template>

<style lang="scss">
    .pk-chat-sidebar-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-4);
        border-radius: var(--rounded-md);
        transition: var(--transition-colors);

        &:hover,
        &:focus-within {
            background-color: var(--color-surface-2);

            .pk-chat-sidebar-item__actions {
                opacity: 1;
            }
        }

        &--active {
            background-color: var(--color-surface-3);

            .pk-chat-sidebar-item__button {
                color: var(--color-word);
            }
        }

        &__button {
            flex: 1;
            min-width: 0;
            padding: var(--spacing-6) var(--spacing-10);
            border: none;
            background: none;
            color: var(--color-word-3);
            font-size: var(--text-14);
            text-align: left;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: pointer;
        }

        &__edit {
            flex: 1;
            min-width: 0;
        }

        &__actions {
            flex-shrink: 0;
            opacity: 0;
            transition: var(--transition-opacity);

            // Touch devices have no hover: keep actions always reachable
            @media (hover: none) {
                opacity: 1;
            }
        }
    }
</style>
